import { useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronDown,
  ChevronUp,
  Circle,
  AlertTriangle,
  Heart,
  Meh,
  Frown,
  Clock,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

// Mock data - in real app this would come from your sentiment analysis API
const sentimentData = {
  current: {
    level: "negative" as "positive" | "neutral" | "negative",
    score: -0.6, // -1 to 1 scale
    confidence: 87,
    trend: "declining" as "improving" | "steady" | "declining",
    lastUpdated: new Date(),
    isLive: true,
  },
  insights: {
    triggerWords: ["frustrated", "disappointed", "waste of time"],
    sessionAverage: -0.2,
    previousSentiments: [
      { time: "14:32", sentiment: "neutral", score: 0.1 },
      { time: "14:35", sentiment: "negative", score: -0.3 },
      { time: "14:38", sentiment: "negative", score: -0.6 },
    ],
  },
  agentPrompt: {
    message:
      "User seems frustrated—respond empathetically and offer concrete solutions.",
    priority: "high" as "low" | "medium" | "high",
  },
};

const getSentimentConfig = (level: string, score: number) => {
  switch (level) {
    case "positive":
      return {
        icon: Heart,
        color: "text-green-600",
        bgColor: "bg-green-50",
        borderColor: "border-green-200",
        label: "Positive",
        emoji: "😊",
      };
    case "negative":
      return {
        icon: Frown,
        color: "text-red-600",
        bgColor: "bg-red-50",
        borderColor: "border-red-200",
        label: "Negative",
        emoji: "😠",
      };
    default:
      return {
        icon: Meh,
        color: "text-yellow-600",
        bgColor: "bg-yellow-50",
        borderColor: "border-yellow-200",
        label: "Neutral",
        emoji: "😐",
      };
  }
};

const getTrendIcon = (trend: string) => {
  switch (trend) {
    case "improving":
      return { icon: TrendingUp, color: "text-green-600" };
    case "declining":
      return { icon: TrendingDown, color: "text-red-600" };
    default:
      return { icon: Minus, color: "text-gray-500" };
  }
};

export function SentimentCard() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);

  const { current, insights, agentPrompt } = sentimentData;
  const sentimentConfig = getSentimentConfig(current.level, current.score);
  const trendConfig = getTrendIcon(current.trend);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isCollapsed) {
    return (
      <Card className="w-full  mx-auto  shadow-sm">
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  "p-2 rounded-full",
                  sentimentConfig.bgColor,
                  sentimentConfig.borderColor,
                  "border"
                )}
              >
                <sentimentConfig.icon
                  className={cn("h-4 w-4", sentimentConfig.color)}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xl">{sentimentConfig.emoji}</span>
                <div>
                  <span
                    className={cn(
                      "font-semibold text-sm",
                      sentimentConfig.color
                    )}
                  >
                    {sentimentConfig.label}
                  </span>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <trendConfig.icon
                      className={cn("h-3 w-3", trendConfig.color)}
                    />
                    <span className={cn("capitalize", trendConfig.color)}>
                      {current.trend}
                    </span>
                    {current.isLive && (
                      <>
                        <span>•</span>
                        <Circle className="h-2 w-2 fill-green-500 text-green-500 animate-pulse" />
                        <span className="text-green-600 font-medium">LIVE</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCollapsed(false)}
              className="h-8 w-8 p-0"
              title="Expand sentiment details"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full  mx-auto shadow-sm">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className={cn(
                "p-2 rounded-full",
                sentimentConfig.bgColor,
                sentimentConfig.borderColor,
                "border"
              )}
            >
              <sentimentConfig.icon
                className={cn("h-4 w-4", sentimentConfig.color)}
              />
            </div>
            <div>
              <h3 className="font-semibold text-sm">User Sentiment</h3>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>Updated {formatTime(current.lastUpdated)}</span>
                {current.isLive && (
                  <div className="flex items-center gap-1">
                    <Circle className="h-2 w-2 fill-green-500 text-green-500 animate-pulse" />
                    <span className="text-green-600 font-medium">LIVE</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowInsights(!showInsights)}
              className="h-8 w-8 p-0"
              title="Toggle insights"
            >
              {showInsights ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsCollapsed(true)}
              className="h-8 w-8 p-0"
              title="Collapse to minimal view"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Main Sentiment Display */}
        <div
          className={cn(
            "p-4 rounded-lg border",
            sentimentConfig.bgColor,
            sentimentConfig.borderColor
          )}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{sentimentConfig.emoji}</span>
              <span className={cn("font-semibold", sentimentConfig.color)}>
                {sentimentConfig.label}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <trendConfig.icon className={cn("h-4 w-4", trendConfig.color)} />
              <span
                className={cn(
                  "text-sm font-medium capitalize",
                  trendConfig.color
                )}
              >
                {current.trend}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Confidence</span>
              <span className="font-medium">{current.confidence}%</span>
            </div>
            <Progress value={current.confidence} className="h-2" />
          </div>
        </div>

        {/* Agent Prompt */}
        {showPrompt && (
          <div
            className={cn(
              "p-3 rounded-lg border-l-4",
              agentPrompt.priority === "high"
                ? "bg-red-50 border-l-red-500"
                : agentPrompt.priority === "medium"
                ? "bg-yellow-50 border-l-yellow-500"
                : "bg-blue-50 border-l-blue-500"
            )}
          >
            <div className="flex items-start gap-2">
              <AlertTriangle
                className={cn(
                  "h-4 w-4 mt-0.5 flex-shrink-0",
                  agentPrompt.priority === "high"
                    ? "text-red-600"
                    : agentPrompt.priority === "medium"
                    ? "text-yellow-600"
                    : "text-blue-600"
                )}
              />
              <div className="flex-1">
                <p className="text-sm font-medium mb-1">Agent Guidance</p>
                <p className="text-sm text-muted-foreground">
                  {agentPrompt.message}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPrompt(false)}
                className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
              >
                ×
              </Button>
            </div>
          </div>
        )}

        {/* Expandable Insights */}
        {showInsights && (
          <div className="space-y-4 pt-2">
            <Separator />

            {/* Trigger Words */}
            <div>
              <h4 className="text-sm font-medium mb-2">Trigger Keywords</h4>
              <div className="flex flex-wrap gap-1">
                {insights.triggerWords.map((word, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {word}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Session History */}
            <div>
              <h4 className="text-sm font-medium mb-2">Recent History</h4>
              <div className="space-y-2">
                {insights.previousSentiments.map((item, index) => {
                  const config = getSentimentConfig(item.sentiment, item.score);
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">
                          {item.time}
                        </span>
                        <config.icon className={cn("h-3 w-3", config.color)} />
                        <span className={config.color}>{config.label}</span>
                      </div>
                      <span className="text-muted-foreground">
                        {item.score > 0 ? "+" : ""}
                        {item.score.toFixed(1)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Session Average */}
            <div className="p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Session Average</span>
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "text-sm font-medium",
                      insights.sessionAverage > 0
                        ? "text-green-600"
                        : insights.sessionAverage < 0
                        ? "text-red-600"
                        : "text-yellow-600"
                    )}
                  >
                    {insights.sessionAverage > 0 ? "+" : ""}
                    {insights.sessionAverage.toFixed(1)}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {insights.sessionAverage > 0
                      ? "Positive"
                      : insights.sessionAverage < 0
                      ? "Negative"
                      : "Neutral"}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
